import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { ArrowRight, Building2, Search, Coins, CheckCircle2 } from "lucide-react";
import { t } from "../lib/i18n";

export default function Landing() {
  return (
    <Layout>
      {/* HERO */}
      <section className="hairline-b">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 space-y-8 fade-up">
            <span className="font-mono-label text-gold">{t("Plataforma · Search Funds · España")}</span>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {t("Donde el")}{" "}<em className="text-gold not-italic font-serif-display">{t("talento")}</em>{" "}{t("compra empresas, y el")}{" "}<em className="text-forest not-italic font-serif-display">{t("capital")}</em>{" "}{t("las financia.")}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl">
              {t("Relevo conecta vendedores de empresas, searchers cualificados e inversores en un único proceso digital, guiado paso a paso como en una adquisición profesional.")}
            </p>
            <div className="flex flex-wrap gap-3 pt-4">
              <Link to="/login">
                <Button
                  className="rounded-none bg-[#C25F1E] hover:bg-[#A94F15] text-white font-semibold h-12 px-7 text-sm tracking-wide"
                  data-testid="cta-primary-start"
                >
                  {t("Empezar ahora")}{" "}<ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#como-funciona">
                <Button
                  variant="outline"
                  className="rounded-none border-[#1E3A5F] text-[#1E3A5F] hover:bg-secondary h-12 px-7"
                  data-testid="cta-how-it-works"
                >
                  {t("Cómo funciona")}
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap gap-x-10 gap-y-4 pt-10 hairline-t pt-8">
              <Stat label={t("Empresas en cartera")} value="120+" />
              <Stat label={t("Searchers activos")} value="48" />
              <Stat label={t("Capital disponible")} value="€85M" />
            </div>
          </div>

          <div className="lg:col-span-5 fade-up-delay-2">
            <div className="hairline aspect-[4/5] overflow-hidden bg-secondary relative" data-testid="hero-image">
              <img
                src="/images/hero-skyline.jpg"
                alt={t("Distrito financiero moderno")}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/85 via-[#1E3A5F]/30 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-[#E0712C]/25 mix-blend-multiply"></div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-orange"></div>
            </div>
            <div className="hairline border-t-0 p-5 bg-bone">
              <span className="font-mono-label text-muted-foreground">{t("Madrid · 2026")}</span>
              <p className="font-serif-display text-xl mt-1">
                {t("\"Conectamos a empresarios que quieren vender su empresa con emprendedores que quieren adquirirla y darle continuidad.\"")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section id="como-funciona" className="hairline-b">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-wrap justify-between items-end gap-6 mb-14">
            <div>
              <span className="font-mono-label text-gold">{t("01 · Tres partes, un proceso")}</span>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 max-w-2xl">
                {t("Una plataforma que guía cada parte de la transacción.")}
              </h2>
            </div>
            <p className="font-mono-label text-muted-foreground max-w-sm">
              {t("El sistema valida, cualifica y empareja oportunidades, capital y talento.")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 hairline border-r-0 border-b-0">
            <Pillar
              num="I"
              icon={<Building2 className="w-5 h-5" />}
              title={t("Vendedores")}
              role="Vende tu empresa con discreción"
              points={[
                "Proceso guiado en 10 etapas, del perfil al cierre",
                "Solo divulgas cuando hay interés cualificado",
                "Mensajes de IA que te guían hasta cerrar",
              ]}
            />
            <Pillar
              num="II"
              icon={<Search className="w-5 h-5" />}
              title={t("Searchers")}
              role="Encuentra tu próxima adquisición"
              points={[
                "Marketplace de empresas pre-cualificadas",
                "Pipeline visual: NDA → IOI → LOI → DD → Closing",
                "Scoring inteligente por sector y EBITDA",
              ]}
            />
            <Pillar
              num="III"
              icon={<Coins className="w-5 h-5" />}
              title={t("Inversores")}
              role="Acceso al deal flow más exclusivo"
              points={[
                "Proyectos de inversión validados",
                "Equity y deuda en un solo canal",
                "Términos anonimizados antes de NDA",
              ]}
            />
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="hairline-b bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="font-mono-label text-gold">{t("02 · El recorrido")}</span>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              {t("De la primera idea al cierre del deal, en 10 etapas.")}
            </h2>
            <p className="text-muted-foreground mt-6">
              {t("Cada etapa desbloquea la siguiente. Cuanta más información valide el vendedor, más visible se vuelve para searchers e inversores cualificados. A partir de la etapa 7, el proceso avanza al ritmo de la negociación con el searcher.")}
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="hairline">
              {[
                ["Etapa 1", "Información Básica", "Sector, geografía, tamaño."],
                ["Etapa 2", "Financieros", "Ingresos, EBITDA, margen, crecimiento."],
                ["Etapa 3", "Cualificación", "Recurrencia, concentración, ventaja competitiva."],
                ["Etapa 4", "Teaser", "Documento ciego para captar searchers."],
                ["Etapa 5", "Information Memorandum", "IM dinámico con proyección financiera, bajo NDA aprobado."],
                ["Etapa 6", "Información para Análisis", "Q&A privado con searchers e inversores, con adjuntos."],
                ["Etapa 7", "Oferta Indicativa (LOI)", "Recepción y negociación de la carta de intenciones."],
                ["Etapa 8", "Due Diligence", "Auditoría profunda del negocio."],
                ["Etapa 9", "Revisión de Contratos", "Redacción y validación legal de los acuerdos finales."],
                ["Etapa 10", "Cierre de la Operación", "Firma y transferencia de la propiedad."],
              ].map(([etapa, name, desc], i) => (
                <div key={i} className={`grid grid-cols-12 gap-4 px-5 py-5 ${i > 0 ? "hairline-t" : ""}`} data-testid={`journey-step-${i + 1}`}>
                  <span className="col-span-2 md:col-span-2 font-mono-label text-gold">{t(etapa)}</span>
                  <span className="col-span-5 md:col-span-3 font-serif-display text-lg">{t(name)}</span>
                  <span className="col-span-12 md:col-span-7 text-sm text-muted-foreground">{t(desc)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center">
          <span className="font-mono-label text-gold">{t("Únete a la plataforma")}</span>
          <h2 className="font-serif-display text-5xl md:text-6xl mt-4 max-w-3xl mx-auto leading-tight">
            {t("Tu próxima adquisición empieza con un perfil verificado.")}
          </h2>
          <div className="mt-10">
            <Link to="/login">
              <Button
                className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-8"
                data-testid="cta-bottom-start"
              >
                {t("Crear cuenta con Google")}{" "}<ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-serif-display text-3xl">{value}</div>
    <div className="font-mono-label text-muted-foreground mt-1">{label}</div>
  </div>
);

const Pillar = ({ num, icon, title, role, points, soon }) => (
  <div className="hairline border-l-0 border-t-0 p-8 lg:p-10 bg-card relative">
    <div className="flex items-center justify-between mb-6">
      <span className="font-serif-display text-4xl text-gold">{num}</span>
      <div className="w-9 h-9 hairline flex items-center justify-center">{icon}</div>
    </div>
    <h3 className="font-serif-display text-2xl">{title}</h3>
    <p className="text-sm text-muted-foreground mt-1 mb-6">{t(role)}</p>
    <ul className="space-y-3">
      {points.map((p, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 flex-shrink-0" />
          <span>{t(p)}</span>
        </li>
      ))}
    </ul>
    {soon && (
      <span className="absolute top-4 right-4 font-mono-label bg-secondary px-2 py-1">{t("Fase 2")}</span>
    )}
  </div>
);
